import {addDoc, collection, doc, getDoc, getDocs, orderBy, query, setDoc} from 'firebase/firestore';
import {db} from '../../../shared/services/firebase';
import {fail, logError, ok, toConfigError} from './apiHelpers';

/**
 * Fetch all saved configurations for an edge, sorted newest first.
 * Also fetches the active config to identify which one is active.
 */
export const getConfigs = async (edgeId) => {
  try {
    const q = query(collection(db, 'edges', edgeId, 'configurations'), orderBy('created', 'desc'));
    const snapshot = await getDocs(q);
    const configs = [];
    snapshot.forEach((d) => {
      if (d.id !== 'configText') {
        configs.push({ref: d.ref, created: d.data().created, value: d.data().value, note: d.data().note || ''});
      }
    });
    configs.sort((a, b) => b.created - a.created);

    const activeSnap = await getDoc(doc(db, 'edges', edgeId, 'activeConfig', 'v1'));
    const activeConfig = activeSnap.exists() ? activeSnap.data().created.toDate() : null;

    return ok({configs, activeConfig});
  } catch (error) {
    const errorInfo = toConfigError(error, 'CONFIG_LOAD_FAILED');
    logError('getConfigs', errorInfo);
    return fail(errorInfo);
  }
};

/**
 * Save new configuration snapshot (does not activate).
 */
export const saveConfig = async (edgeId, jsonText, note) => {
  try {
    await addDoc(collection(db, 'edges', edgeId, 'configurations'), {
      value: jsonText,
      created: new Date(),
      ...(note ? {note} : {}),
    });
    return ok();
  } catch (error) {
    const errorInfo = toConfigError(error, 'CONFIG_SAVE_FAILED');
    logError('saveConfig', errorInfo);
    return fail(errorInfo);
  }
};

/**
 * Save new configuration snapshot and immediately activate it.
 */
export const saveAndActivateConfig = async (edgeId, jsonText, note) => {
  try {
    const currentTime = new Date();
    await addDoc(collection(db, 'edges', edgeId, 'configurations'), {
      value: jsonText,
      created: currentTime,
      ...(note ? {note} : {}),
    });
    await setDoc(doc(db, 'edges', edgeId, 'activeConfig', 'v1'), {
      value: jsonText,
      created: currentTime,
    });
    return ok();
  } catch (error) {
    const errorInfo = toConfigError(error, 'CONFIG_SAVE_FAILED');
    logError('saveAndActivateConfig', errorInfo);
    return fail(errorInfo);
  }
};

/**
 * Activate an existing saved configuration snapshot.
 */
export const activateConfig = async (edgeId, configValue, configCreated) => {
  try {
    await setDoc(doc(db, 'edges', edgeId, 'activeConfig', 'v1'), {
      value: configValue,
      created: configCreated,
    });
    return ok();
  } catch (error) {
    const errorInfo = toConfigError(error, 'CONFIG_SAVE_FAILED');
    logError('activateConfig', errorInfo);
    return fail(errorInfo);
  }
};

